import clsx from "clsx";
import { MediaItem, SlideItemProps } from "../../types";
import { motion } from "framer-motion";
import parse from "html-react-parser";
import WButton from "@/components/wbutton/WButton";
import { useRouter } from "next/router";
import { useEffect, useRef } from "react";
import Image from "next/image";

export const SlideItem = (props: SlideItemProps) => {
  const router = useRouter();
  const videoRef = useRef<HTMLVideoElement>(null);

  const isVideo = (media: MediaItem) => {
    return (
      media?.mime?.match(/\.(mp4|webm|ogg|mov|mpg|mpeg|avi|wmv|flv|mkv)$/i) ||
      media?.mime?.includes("video")
    );
  };

  const currentImage =
    props?.mobileImage?.url && window.innerWidth < 1024
      ? props.mobileImage
      : props.image;

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.log("Video autoplay failed:", error);
      });
    }
  }, []);

  return (
    <div
      id={props?.id}
      className={clsx([
        `relative w-full flex-1 pt-5 pb-10 h-full lg:pb-0`,
        !props?.bgColor && "bg-black",
      ])}
      style={{
        backgroundColor: props?.bgColor ? props?.bgColor : undefined,
      }}
    >
      {!isVideo(currentImage) && currentImage?.url && (
        <div className="absolute inset-0 z-0 overflow-hidden">
          <Image
            src={currentImage.url}
            alt={currentImage?.alt || "Hero image"}
            fill
            priority={true}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 100vw, 100vw"
            style={{
              objectFit: "cover",
              objectPosition: currentImage?.position || "center center",
            }}
            quality={85}
          />
        </div>
      )}

      {isVideo(currentImage) && (
        <video
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
        >
          <source src={currentImage?.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      )}
      <div className="container relative h-full z-20 flex-1 flex flex-col gap-10 px-4 py-5 mx-auto lg:py-20 lg:max-w-7xl">
        <div className="relative z-20 h-full flex-1 lg:min-h-[75vh] flex items-center gap-0 lg:flex-row flex-col-reverse">
          <motion.div
            initial={{ opacity: 0, y: -30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false }}
            transition={{ duration: 1, type: "spring", delay: 0.1 }}
            className={clsx(
              "relative flex flex-col items-center w-full gap-10 lg:items-start lg:w-auto",
              props?.box && "p-8 rounded-lg"
            )}
          >
            <div
              className="absolute inset-0 rounded-xl"
              style={{
                backgroundColor: props.box ? props.boxColor : "",
                opacity: 0.8,
              }}
            />
            <div
              className={clsx(
                "relative z-10 flex flex-col items-center w-full gap-10 lg:items-start lg:w-auto",
                props?.box && !props?.textColor && "text-white"
              )}
              style={{ color: props?.textColor }}
            >
              {props?.topTitle && (
                <div
                  className={clsx(
                    "-mb-10 text-2xl font-bold pb-4 self-start",
                    props?.textShadow && "text-shadow-lg"
                  )}
                  style={
                    props?.textShadow
                      ? {
                          textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)",
                        }
                      : {}
                  }
                >
                  {props?.topTitle}
                </div>
              )}
              <div
                className={clsx(
                  "lg:text-7xl text-4xl font-bold self-start",
                  props?.textShadow && "text-shadow-lg"
                )}
                style={
                  props?.textShadow
                    ? {
                        textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
                      }
                    : {}
                }
              >
                {parse(props?.mainTitle)}
              </div>
              <div
                className={clsx(
                  "lg:w-2/3 text-2xl lg:text-3xl",
                  props?.textShadow && "text-shadow-lg"
                )}
                style={
                  props?.textShadow
                    ? {
                        textShadow: "2px 2px 4px rgba(0, 0, 0, 0.8)",
                      }
                    : {}
                }
              >
                {props?.secondaryTitle}
              </div>
              {props?.button?.text && props?.button?.url && (
                <WButton
                  text={props?.button.text}
                  color={props?.button?.color}
                  textColor={props?.button?.textColor}
                  type="primary-red-btn"
                  className="w-fit"
                  onClick={() => {
                    props?.button?.url && router?.push(props?.button?.url);
                  }}
                />
              )}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: false }}
            transition={{ duration: 0.5, type: "spring", delay: 0.2 }}
            className="flex-shrink-0 mb-auto w-52 justify-self-start"
          ></motion.div>
        </div>
      </div>
    </div>
  );
};
